import React, { useState, useEffect, useRef } from 'react';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { BASE_URL } from '../constants/config';
import { showToast, showAlert } from '../components/Toast';

// ── API Helpers ─────────────────────────────────────────────────────────────

async function apiGenerateOTP(mobileNumber) {
  const url = `${BASE_URL}api/User/GenerateOTP?MobileNumber=${mobileNumber}&UserType=EMPLOYEE`;
  const response = await fetch(url, { method: 'POST' });
  if (!response.ok) {
    let msg = `Server returned ${response.status}`;
    try { const body = await response.json(); msg = body?.Message || body?.message || msg; } catch (_) {}
    throw new Error(msg);
  }
}

async function apiVerifyOTP(mobileNumber, otp) {
  const url = `${BASE_URL}api/User/VerifyOTP?MobileNumber=${mobileNumber}&OTP=${otp}`;
  const response = await fetch(url, { method: 'POST' });
  if (!response.ok) return { success: false };

  let supplierId, empCode, empId;
  try {
    const text = await response.text();
    const idMatch = text.match(/ID=(\d+)/i);
    if (idMatch?.[1]) supplierId = idMatch[1];
    const empMatch = text.match(/E=([A-Z]+)-(\d+)/i);
    if (empMatch?.[1] && empMatch?.[2]) { empCode = empMatch[1]; empId = empMatch[2]; }
    else {
      const empMatch2 = text.match(/E=([^\s]+)/i);
      if (empMatch2?.[1]) empCode = empMatch2[1];
    }
  } catch (_) {}
  return { success: true, supplierId, empCode, empId };
}

async function apiLoginUser(username, password) {
  const body = `grant_type=password&username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`;
  const response = await fetch(`${BASE_URL}token`, { method: 'POST', body });
  if (!response.ok) return null;
  const json = await response.json();
  return json ?? null;
}

async function apiFetchAndStoreUserDetails(token) {
  try {
    const response = await fetch(`${BASE_URL}api/UserAccount/GetUserAccountDetail`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return;
    const data = await response.json();
    const isEmployee = data?.IsEmployee ? 'true' : 'false';
    const userName = data?.OwnerName || data?.FirmName || data?.UserName || '';
    localStorage.setItem(STORAGE_KEYS.IS_EMPLOYEE, isEmployee);
    localStorage.setItem(STORAGE_KEYS.USER_NAME, userName);
  } catch (_) {}
}

// ── Component ───────────────────────────────────────────────────────────────

const LoginScreen = ({ onLoginSuccess }) => {
  const [mobile, setMobile] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [step, setStep] = useState('mobile'); // 'mobile' | 'otp'
  const [loading, setLoading] = useState(false);
  const [animating, setAnimating] = useState(false);

  const otpRefs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (step === 'otp') {
      const t = setTimeout(() => otpRefs[0].current?.focus(), 400);
      return () => clearTimeout(t);
    }
  }, [step]);

  const validateMobile = (v) => {
    if (!v.trim()) return 'Mobile number is required.';
    if (!/^\d{10}$/.test(v.trim())) return 'Enter a valid 10-digit mobile number.';
    return '';
  };

  const validateOtp = (digits) => {
    if (digits.some(d => d === '')) return 'Please enter all 4 OTP digits.';
    return '';
  };

  const animateTransition = (callback) => {
    setAnimating(true);
    setTimeout(() => {
      callback();
      setAnimating(false);
    }, 220);
  };

  const handleSendOTP = async () => {
    const err = validateMobile(mobile);
    if (err) { setMobileError(err); return; }
    setMobileError('');
    setLoading(true);
    try {
      await apiGenerateOTP(mobile.trim());
      animateTransition(() => {
        setStep('otp');
        setOtpDigits(['', '', '', '']);
        setOtpError('');
      });
    } catch (e) {
      const msg = e?.message || 'Failed to send OTP. Please try again.';
      if (msg.toLowerCase().includes('not able') || msg.toLowerCase().includes('contact')) {
        showAlert('Number Not Registered', 'This number is not registered with us. Please contact JOJ support to get access.');
      } else {
        showToast(msg, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    const err = validateOtp(otpDigits);
    if (err) { setOtpError(err); return; }
    setOtpError('');
    setLoading(true);
    const otp = otpDigits.join('');
    try {
      const verifyData = await apiVerifyOTP(mobile.trim(), otp);
      if (!verifyData.success) {
        setOtpError('Invalid OTP. Please try again.');
        setLoading(false);
        return;
      }
      const tokenData = await apiLoginUser(mobile.trim(), otp);
      if (!tokenData?.access_token) {
        setOtpError('Login failed. Please try again.');
        setLoading(false);
        return;
      }
      const accessToken = tokenData.access_token;
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
      localStorage.setItem(STORAGE_KEYS.MOBILE, mobile.trim());
      if (verifyData.supplierId) localStorage.setItem(STORAGE_KEYS.SUPPLIER_ID, verifyData.supplierId);
      if (verifyData.empCode) localStorage.setItem(STORAGE_KEYS.EMP_CODE, verifyData.empCode);
      if (verifyData.empId) localStorage.setItem(STORAGE_KEYS.EMP_ID, verifyData.empId);
      apiFetchAndStoreUserDetails(accessToken).catch(() => {});
      onLoginSuccess?.(accessToken);
    } catch (e) {
      setOtpError(e?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (text, index) => {
    const digit = text.replace(/[^0-9]/g, '').slice(-1);
    const updated = [...otpDigits];
    updated[index] = digit;
    setOtpDigits(updated);
    setOtpError('');
    if (digit && index < 3) otpRefs[index + 1].current?.focus();
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && otpDigits[index] === '' && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
    if (e.key === 'Enter' && index === 3) handleVerifyOTP();
  };

  const handleResend = async () => {
    setOtpDigits(['', '', '', '']);
    setOtpError('');
    setLoading(true);
    try {
      await apiGenerateOTP(mobile.trim());
      showToast('OTP sent successfully!', 'success');
      otpRefs[0].current?.focus();
    } catch (e) {
      showToast(e?.message || 'Failed to resend OTP.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    animateTransition(() => {
      setStep('mobile');
      setOtpDigits(['', '', '', '']);
      setOtpError('');
    });
  };

  return (
    <div className="app-shell">
      <div className="screen-content" style={{ background: '#F5F5F5' }}>
        <div className="login-scroll">

          {/* ── Brand Header ─────────────────────────────── */}
          <div className="login-header">
            <div className="logo-circle">
              <span className="logo-text">JOJ</span>
            </div>
            <div className="app-name">JOJEXPENSE</div>
            <div className="tagline">Jewels of Joy · Expense</div>
          </div>

          {/* ── Card ────────────────────────────────────── */}
          <div className={`login-card${animating ? ' login-card--fade' : ''}`}>

            {step === 'mobile' ? (
              <>
                <div className="step-title">Welcome Back 👋</div>
                <div className="step-subtitle">Enter your registered mobile number to receive an OTP.</div>

                <div className="form-group">
                  <label className="form-label">Mobile Number</label>
                  <input
                    id="mobile-input"
                    className={`form-input${mobileError ? ' form-input--error' : ''}`}
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="e.g. 9876543210"
                    value={mobile}
                    onChange={e => {
                      setMobile(e.target.value.replace(/[^0-9]/g, ''));
                      if (mobileError) setMobileError('');
                    }}
                    onKeyDown={e => { if (e.key === 'Enter') handleSendOTP(); }}
                    disabled={loading}
                    autoFocus
                  />
                  {mobileError && <div className="form-error">{mobileError}</div>}
                </div>

                <button
                  id="send-otp-btn"
                  className="btn-primary"
                  onClick={handleSendOTP}
                  disabled={loading}
                  style={{ marginTop: 8 }}
                >
                  {loading ? <div className="spinner" /> : 'Send OTP'}
                </button>
              </>
            ) : (
              <>
                <button className="back-row" onClick={handleBack}>
                  <span className="back-arrow">←</span>
                  <span className="back-text">Change number</span>
                </button>

                <div className="step-title">Verify OTP 🔐</div>
                <div className="step-subtitle">
                  We sent a 4-digit code to{' '}
                  <span style={{ color: '#D4A373', fontWeight: 700 }}>+91 {mobile}</span>
                </div>

                <div className="otp-row">
                  {otpDigits.map((digit, i) => (
                    <input
                      key={i}
                      ref={otpRefs[i]}
                      id={`otp-${i}`}
                      className={`otp-box${digit ? ' otp-box--filled' : ''}${otpError ? ' otp-box--error' : ''}`}
                      type="tel"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleOtpChange(e.target.value, i)}
                      onKeyDown={e => handleOtpKeyDown(e, i)}
                      disabled={loading}
                    />
                  ))}
                </div>
                {otpError && <div className="form-error text-center" style={{ marginBottom: 8 }}>{otpError}</div>}

                <button
                  id="login-btn"
                  className="btn-primary"
                  onClick={handleVerifyOTP}
                  disabled={loading}
                  style={{ marginTop: 8 }}
                >
                  {loading ? <div className="spinner" /> : 'Login'}
                </button>

                <button className="resend-row" onClick={handleResend} disabled={loading}>
                  Didn't receive?{' '}
                  <span style={{ color: '#000', fontWeight: 700 }}> Resend OTP</span>
                </button>
              </>
            )}
          </div>

          {/* ── Footer ──────────────────────────────────── */}
          <a
            href="https://www.jewelsofjoy.in/appprivacypolicy"
            target="_blank"
            rel="noopener noreferrer"
            className="privacy-link"
          >
            Privacy Policy
          </a>
          <div className="footer-text">© 2026 Jewels of Joy. All rights reserved.</div>
        </div>
      </div>

      <style>{`
        .login-scroll {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-height: 100%;
          padding: 40px 24px 48px;
          justify-content: center;
        }
        .login-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 32px;
        }
        .logo-circle {
          width: 72px;
          height: 72px;
          border-radius: 36px;
          background: #C8973A;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
          box-shadow: 0 6px 20px rgba(200,151,58,0.5);
        }
        .logo-text {
          font-size: 24px;
          font-weight: 800;
          color: #000;
          letter-spacing: 1px;
        }
        .app-name {
          font-size: 26px;
          font-weight: 800;
          color: #D4A373;
          letter-spacing: 3px;
        }
        .tagline {
          font-size: 12px;
          color: #757575;
          margin-top: 4px;
          letter-spacing: 1px;
        }
        .login-card {
          background: #fff;
          border-radius: 20px;
          padding: 28px;
          border: 1px solid #E0E0E0;
          box-shadow: 0 8px 32px rgba(0,0,0,0.10);
          width: 100%;
          transition: opacity 0.22s ease;
        }
        .login-card--fade { opacity: 0; }
        .step-title {
          font-size: 22px;
          font-weight: 700;
          color: #000;
          margin-bottom: 6px;
        }
        .step-subtitle {
          font-size: 14px;
          color: #757575;
          margin-bottom: 24px;
          line-height: 1.5;
        }
        .otp-row {
          display: flex;
          gap: 12px;
          margin-bottom: 6px;
        }
        .otp-box {
          flex: 1;
          height: 62px;
          border-radius: 12px;
          border: 1.5px solid #E0E0E0;
          background: #F9F9F9;
          font-size: 26px;
          font-weight: 700;
          color: #000;
          text-align: center;
          transition: border-color 0.18s, box-shadow 0.18s;
          min-width: 0;
          padding: 0;
        }
        .otp-box:focus {
          border-color: #C8973A;
          box-shadow: 0 0 0 3px rgba(200,151,58,0.15);
          background: #fff;
          outline: none;
        }
        .otp-box--filled { border-color: #C8973A; background: #FFF8E7; }
        .otp-box--error { border-color: #c62828; }
        .back-row {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 20px;
          background: none;
          color: #000;
          font-size: 13px;
          font-weight: 600;
          padding: 0;
          cursor: pointer;
        }
        .back-arrow { font-size: 20px; }
        .resend-row {
          margin-top: 20px;
          text-align: center;
          font-size: 13px;
          color: #757575;
          background: none;
          width: 100%;
          padding: 8px;
          cursor: pointer;
        }
        .privacy-link {
          display: block;
          color: #000;
          font-size: 12px;
          font-weight: 600;
          text-decoration: underline;
          margin-top: 32px;
        }
        .footer-text {
          text-align: center;
          color: #757575;
          font-size: 11px;
          margin-top: 16px;
          opacity: 0.6;
        }
      `}</style>
    </div>
  );
};

export default LoginScreen;
